import React, { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import styles from "asset/styles/quotation/quotationPreviewV2.module.scss"
import Loader from "component/loader/loaderComponent"
import { isHtmlEmpty } from "component/richTextEditor/RichTextEditor"
import DuplicateQuotationModal, {
	type DuplicateQuotationSourceData,
	readDuplicateSourceMealGuideFlags,
} from "component/quotation/DuplicateQuotationModal"
import DuplicateQuotationSkippedHotelsPopup from "component/quotation/duplicateQuotationSkippedHotelsPopup"
import useDuplicateSkippedHotelsNotice from "component/quotation/useDuplicateSkippedHotelsNotice"
import CreateVersionReasonPopup from "component/quotation/createVersionReasonPopup"
import useCreateQuotationVersion from "component/quotation/useCreateQuotationVersion"
import MarkQuotationStatusConfirmPopup, {
	type MarkQuotationStatusConfirmVariant,
} from "component/quotation/MarkQuotationStatusConfirmPopup"
import { type QuotationStatusVisualVariant, variantFromKnownName } from "component/quotation/quotationStatusVariant"
import QuotationStatusPill from "component/quotationStatusBadge/QuotationStatusPill"
import { toTitleCase } from "util/textcase"
import { FLAG_QUOTATION_VERSIONS } from "util/featureFlags"
import { getQuotationPreviewHotelRows, quotationIdsEqual } from "pages/quotation/quotationPreviewUtil"
import useQuotationPreviewV2 from "component/quotationPreviewV2/useQuotationPreviewV2"
import QuotationPreviewV2MoreActions from "component/quotationPreviewV2/quotationPreviewV2MoreActions"
import QuotationPreviewV2Pricing from "component/quotationPreviewV2/quotationPreviewV2Pricing"
import { buildDayCosts } from "component/quotationPreviewV2/quotationPreviewV2PricingUtil"
import {
	QuotationPreviewV2Hotels,
	QuotationPreviewV2InclusionsExclusions,
	QuotationPreviewV2Itinerary,
	QuotationPreviewV2Overview,
	QuotationPreviewV2Tabs,
	QuotationPreviewV2VersionBar,
} from "component/quotationPreviewV2/quotationPreviewV2Components"
import type {
	IQuotationPreviewV2Tab,
	QuotationPreviewV2ItineraryMode,
	QuotationPreviewV2TabKey,
} from "component/quotationPreviewV2/quotationPreviewV2Types"
import {
	buildCitiesView,
	buildCreationDateText,
	buildItineraryRows,
	buildPackagePrices,
	buildPaxCounts,
	buildTextLines,
	buildTravelDatesText,
	buildVersionChips,
	formatUsd,
	resolveAgencyName,
	textOrNotProvided,
} from "component/quotationPreviewV2/quotationPreviewV2Util"

const QuotationPreviewV2: React.FC = () => {

	const navigate = useNavigate()
	const { quotation_id = "" } = useParams()
	const preview = useQuotationPreviewV2(quotation_id)
	const { quotation, quotationData, versions, currentVersion, currentVersionNo } = preview

	const [activeTab, setActiveTab] = useState<QuotationPreviewV2TabKey>("itinerary")
	const [itineraryMode, setItineraryMode] = useState<QuotationPreviewV2ItineraryMode>("draft")
	const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)
	const [statusConfirmVariant, setStatusConfirmVariant] = useState<MarkQuotationStatusConfirmVariant | null>(null)
	const skippedHotelsNotice = useDuplicateSkippedHotelsNotice(!preview.isLoading && preview.isLoaded)
	const createVersion = useCreateQuotationVersion({ quotationId: quotation_id })

	const days = useMemo(() => quotationData?.data?.journey_details?.days || [], [quotationData?.data?.journey_details?.days])
	const pax = useMemo(() => buildPaxCounts(quotation, quotationData?.data?.children || []), [quotation, quotationData?.data?.children])
	const cities = useMemo(() => buildCitiesView(days), [days])
	const itineraryRows = useMemo(() => buildItineraryRows(days, isHtmlEmpty), [days])
	const hotelRows = useMemo(() => getQuotationPreviewHotelRows(days), [days])
	const inclusionLines = useMemo(
		() => buildTextLines(quotationData?.data?.inclusion_lines, quotation?.inclusion_lines),
		[quotationData?.data?.inclusion_lines, quotation?.inclusion_lines],
	)
	const exclusionLines = useMemo(
		() => buildTextLines(quotationData?.data?.exclusion_lines, quotation?.exclusion_lines),
		[quotationData?.data?.exclusion_lines, quotation?.exclusion_lines],
	)
	const packagePrices = useMemo(
		() => buildPackagePrices(quotationData?.data?.pricing_summary?.category_breakdown),
		[quotationData?.data?.pricing_summary?.category_breakdown],
	)
	const dayCosts = useMemo(() => buildDayCosts(days, quotation), [days, quotation])
	const totalPriceText = formatUsd(quotationData?.data?.pricing_summary?.final_total_usd)
	const versionBadge = preview.flagViewingLatest ? `V${currentVersionNo} · Latest` : `V${currentVersionNo}`
	const versionChips = useMemo(
		() => buildVersionChips(versions, String(currentVersion?.quotation_id || quotation_id), quotationIdsEqual),
		[versions, currentVersion, quotation_id],
	)

	const contactDetails = [
		{ label: "Handled by", value: toTitleCase(quotation?.created_by || quotation?.handled_by) || "-" },
		{ label: "Agency name", value: toTitleCase(resolveAgencyName(quotation)) },
		{ label: "Agent name", value: toTitleCase(textOrNotProvided(quotation?.agent_name)) },
		{ label: "Phone", value: textOrNotProvided(quotation?.agent_contact_number) },
		{ label: "Email", value: textOrNotProvided(quotation?.agent_email) },
		{ label: "Quotation creation date", value: buildCreationDateText(quotation) },
	]

	const tabs: IQuotationPreviewV2Tab[] = [
		{ key: "itinerary", label: "Itinerary", flagEnabled: true },
		{ key: "pricing", label: "Pricing", flagEnabled: true },
		{ key: "hotels", label: "Hotels", flagEnabled: true },
		{ key: "inclusionsExclusions", label: "Inclusions and Exclusions", flagEnabled: true },
		...(FLAG_QUOTATION_VERSIONS ? [{ key: "versions" as const, label: "Versions", badge: versions.length, flagEnabled: false }] : []),
	]

	const duplicateSourceData: DuplicateQuotationSourceData = useMemo(() => {
		const mealGuideFlags = readDuplicateSourceMealGuideFlags(quotationData?.data, quotation)
		return {
			agency_id: quotation?.agency_id || "",
			agent_id: quotation?.agent_id || "",
			foc_pax_number: Number(quotation?.foc_pax_number || 0) || 0,
			number_of_adults: Number(quotation?.number_of_adults || 0) || 0,
			number_of_children: Number(quotation?.number_of_children || 0) || 0,
			start_date: quotation?.start_date || "",
			number_of_nights: Number(quotation?.number_of_nights || 0) || 0,
			flag_with_tip: mealGuideFlags.flag_with_tip,
			flag_all_days_have_dinner: mealGuideFlags.flag_all_days_have_dinner,
			quotation_status_id: quotation?.quotation_status_id ?? 1,
		}
	}, [quotationData?.data, quotation])

	const handleDuplicateConfirm = async (payload: any) => {
		if (await preview.duplicateQuotation(payload)) setIsDuplicateOpen(false)
	}

	const handleConfirmStatusChange = async (remark?: string) => {
		if (!statusConfirmVariant) return
		if (await preview.changeStatus(statusConfirmVariant, remark)) setStatusConfirmVariant(null)
	}

	return (
		<div className={styles.page}>
			{preview.isLoading && <Loader message="Loading quotation..." overlay={true} />}

			<QuotationPreviewV2Overview
				staCode={String(quotation?.sta_code || "")}
				statusSlot={quotation && (
					<QuotationStatusPill
						status_name={quotation.quotation_status_name}
						status_id={quotation.quotation_status_id}
						variant={variantFromKnownName(quotation.quotation_status_name) as QuotationStatusVisualVariant}
						quotation_id={quotation_id}
						sta_code={quotation.sta_code}
						flagViewOnly={false}
						className={styles.statusPill}
						dropdownOptionClassName={styles.statusMenuOption}
						flagMatchAnchorWidth
					/>
				)}
				actionsSlot={(
					<QuotationPreviewV2MoreActions
						flagDownloadDisabled={preview.flagDraft}
						flagShowMarkConfirmedLost={preview.flagCanMarkConfirmedLost}
						flagBusy={preview.isSubmitting}
						onDuplicate={() => setIsDuplicateOpen(true)}
						onDownload={preview.handleDownload}
						onMarkConfirmed={() => setStatusConfirmVariant("confirmed")}
						onMarkLost={() => setStatusConfirmVariant("lost")}
					/>
				)}
				contactDetails={contactDetails}
				travelDatesText={buildTravelDatesText(quotation?.start_date, quotation?.number_of_nights)}
				pax={pax}
				cities={cities}
				totalPriceText={totalPriceText}
				currentVersionNo={currentVersionNo}
				onBack={() => navigate("/quotation")}
				onEdit={preview.handleEdit}
			/>

			<QuotationPreviewV2VersionBar
				currentVersionNo={currentVersionNo}
				chips={versionChips}
				flagShowCompare={FLAG_QUOTATION_VERSIONS}
				flagShowNewVersion={preview.flagCanCreateVersion}
				flagBusy={preview.isSubmitting || createVersion.isSubmitting}
				onSelectVersion={preview.goToVersion}
				onNewVersion={createVersion.openReasonPopup}
			/>

			<QuotationPreviewV2Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

			<main className={styles.content}>
				{activeTab === "itinerary" && (
					<QuotationPreviewV2Itinerary
						rows={itineraryRows}
						versionBadge={versionBadge}
						mode={itineraryMode}
						onModeChange={setItineraryMode}
					/>
				)}
				{activeTab === "pricing" && (
					<QuotationPreviewV2Pricing
						packagePrices={packagePrices}
						totalPriceText={totalPriceText}
						dayCosts={dayCosts}
						versionBadge={versionBadge}
					/>
				)}
				{activeTab === "hotels" && <QuotationPreviewV2Hotels rows={hotelRows} versionBadge={versionBadge} />}
				{activeTab === "inclusionsExclusions" && (
					<QuotationPreviewV2InclusionsExclusions
						inclusionLines={inclusionLines}
						exclusionLines={exclusionLines}
						versionBadge={versionBadge}
					/>
				)}
			</main>

			<DuplicateQuotationModal
				isOpen={isDuplicateOpen}
				onClose={() => setIsDuplicateOpen(false)}
				source={duplicateSourceData}
				isSubmitting={preview.isSubmitting}
				onConfirm={handleDuplicateConfirm}
			/>
			<DuplicateQuotationSkippedHotelsPopup {...skippedHotelsNotice} />
			<CreateVersionReasonPopup
				{...createVersion.reasonPopup}
				fromVersionNo={currentVersionNo}
				nextVersionNo={Math.max(preview.latestVersionNo, currentVersionNo) + 1}
			/>
			<DuplicateQuotationSkippedHotelsPopup {...createVersion.skippedHotelsPopup} />
			<MarkQuotationStatusConfirmPopup
				isOpen={Boolean(statusConfirmVariant)}
				variant={statusConfirmVariant || "confirmed"}
				staCode={String(quotation?.sta_code || "")}
				isSubmitting={preview.isSubmitting}
				onCancel={() => setStatusConfirmVariant(null)}
				onConfirm={handleConfirmStatusChange}
			/>
		</div>
	)
}

export default QuotationPreviewV2
